import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Vault,
  Upload,
  Download,
  Trash2,
  FileText,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";
import {
  isVaultCreated,
  createVault,
  addDocument,
  listDocuments,
  getDocumentBlob,
  deleteDocument,
  renameDocument,
  formatBytes,
} from "@/lib/coffre";
import { getRemaining, canAnalyze, consume, DAILY_LIMIT } from "@/lib/analyzeLimit";
import { documentToBase64 } from "@/lib/imageProcess";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  listByLocation,
  moveToArchive,
  deleteLetter,
  renameLetter,
  LOCATIONS,
} from "@/lib/lettersStore";
import { parseLetter } from "@/lib/parseLetter";
import { Archive, CheckCircle2, Copy, Inbox, Pencil, FileCheck, FileArchive } from "lucide-react";
import jsPDF from "jspdf";
import JSZip from "jszip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoffreModal({ open, onClose, onLetterGenerated }) {
  const [created, setCreated] = useState(false);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [remaining, setRemaining] = useState(DAILY_LIMIT);
  const [confirmState, setConfirmState] = useState(null); // { type, doc, letterId }
  const [tab, setTab] = useState("documents"); // documents | ready | archive
  const [readyLetters, setReadyLetters] = useState([]);
  const [archived, setArchived] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [renameDocValue, setRenameDocValue] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemaining(getRemaining());
    refreshLetters();
    (async () => {
      const c = await isVaultCreated();
      setCreated(c);
      if (c) await refresh();
    })();
  }, [open]);

  const refresh = async () => {
    const list = await listDocuments();
    setDocs(list);
  };

  const refreshLetters = () => {
    setReadyLetters(listByLocation(LOCATIONS.READY));
    setArchived(listByLocation(LOCATIONS.ARCHIVED));
  };

  // Auto-switch vers l'onglet "Lettre prête" si une nouvelle lettre arrive pendant que le modal est ouvert
  useEffect(() => {
    if (!open) return;
    const iv = setInterval(refreshLetters, 800);
    return () => clearInterval(iv);
  }, [open]);

  const handleValidateReady = (id) => {
    moveToArchive(id);
    refreshLetters();
    toast.success("Lettre archivée dans le stockage.");
  };

  const handleDeleteLetter = (id) => {
    setConfirmState({ type: "delete-letter", letterId: id });
  };

  const confirmDeleteLetter = () => {
    const id = confirmState?.letterId;
    setConfirmState(null);
    if (!id) return;
    deleteLetter(id);
    refreshLetters();
    toast.success("Lettre supprimée.");
  };

  const handleCopyLetter = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Lettre copiée dans le presse-papiers.");
    } catch {
      toast.error("Impossible de copier.");
    }
  };

  const handleDownloadLetter = (record) => {
    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const marginX = 56;
      const marginTop = 72;
      const maxWidth = 595.28 - marginX * 2;
      pdf.setFont("times", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(20, 20, 20);
      const lines = pdf.splitTextToSize(record.letter, maxWidth);
      let y = marginTop;
      const lineH = 15;
      for (const l of lines) {
        if (y > 780) {
          pdf.addPage();
          y = marginTop;
        }
        pdf.text(l, marginX, y);
        y += lineH;
      }
      const safe = (record.title || "lettre").replace(/[^\p{L}\p{N}\s\-–_]/gu, "").trim();
      pdf.save(`${safe || "lettre"}.pdf`);
    } catch {
      toast.error("Erreur lors du téléchargement PDF.");
    }
  };

  const beginRename = (record) => {
    setRenamingId(record.id);
    setRenameValue(record.title);
  };

  const commitRename = () => {
    if (renamingId) {
      renameLetter(renamingId, renameValue);
      refreshLetters();
      toast.success("Nom mis à jour.");
    }
    setRenamingId(null);
    setRenameValue("");
  };

  const beginRenameDoc = (doc) => {
    setRenamingDocId(doc.id);
    setRenameDocValue(doc.name);
  };

  const commitRenameDoc = async () => {
    if (renamingDocId) {
      try {
        await renameDocument(renamingDocId, renameDocValue);
        await refresh();
        toast.success("Document renommé.");
      } catch {
        toast.error("Impossible de renommer.");
      }
    }
    setRenamingDocId(null);
    setRenameDocValue("");
  };

  const handleDownloadAllZip = async () => {
    if (archived.length === 0) return;
    try {
      const zip = new JSZip();
      const seen = new Map();
      for (const record of archived) {
        // Génère un PDF binaire pour chaque lettre
        const pdf = new jsPDF({ unit: "pt", format: "a4" });
        const marginX = 56;
        const marginTop = 72;
        const maxWidth = 595.28 - marginX * 2;
        pdf.setFont("times", "normal");
        pdf.setFontSize(11);
        pdf.setTextColor(20, 20, 20);
        const lines = pdf.splitTextToSize(record.letter, maxWidth);
        let y = marginTop;
        for (const l of lines) {
          if (y > 780) {
            pdf.addPage();
            y = marginTop;
          }
          pdf.text(l, marginX, y);
          y += 15;
        }
        const blob = pdf.output("blob");
        const rawName = (record.title || "lettre").replace(/[^\p{L}\p{N}\s\-–_]/gu, "").trim() || "lettre";
        // Évite les doublons de nom
        const count = (seen.get(rawName) || 0) + 1;
        seen.set(rawName, count);
        const safeName = count === 1 ? `${rawName}.pdf` : `${rawName} (${count}).pdf`;
        zip.file(safeName, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `Zerux-Stockage-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`${archived.length} lettre${archived.length > 1 ? "s" : ""} exportée${archived.length > 1 ? "s" : ""} en ZIP.`);
    } catch (e) {
      toast.error("Erreur lors de la création du ZIP.");
    }
  };

  const handleCreate = async () => {
    setBusy(true);
    try {
      await createVault();
      setCreated(true);
      toast.success("Le coffre est créé et prêt à recevoir vos documents.");
    } catch {
      toast.error("Impossible de créer le coffre.");
    } finally {
      setBusy(false);
    }
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        await addDocument(f);
      }
      await refresh();
      toast.success(
        files.length === 1
          ? "Document stocké dans votre coffre."
          : `${files.length} documents stockés dans votre coffre.`
      );
    } catch {
      toast.error("Erreur lors du stockage.");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await getDocumentBlob(doc.id);
      if (!blob) return toast.error("Document introuvable.");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Erreur de téléchargement.");
    }
  };

  const handleDelete = async (doc) => {
    setConfirmState({ type: "delete", doc });
  };

  const confirmDelete = async () => {
    const doc = confirmState?.doc;
    setConfirmState(null);
    if (!doc) return;
    try {
      await deleteDocument(doc.id);
      await refresh();
      toast.success("Document supprimé.");
    } catch {
      toast.error("Erreur de suppression.");
    }
  };

  const handleAnalyze = async (doc) => {
    if (!canAnalyze()) {
      toast.error(`Limite quotidienne atteinte (${DAILY_LIMIT} analyses/jour). Réessayez demain à 00 h 00.`);
      return;
    }
    setConfirmState({ type: "analyze", doc });
  };

  const confirmAnalyze = async () => {
    const doc = confirmState?.doc;
    setConfirmState(null);
    if (!doc) return;

    setAnalyzingId(doc.id);
    try {
      const blob = await getDocumentBlob(doc.id);
      if (!blob) throw new Error("Document introuvable.");
      const file = new File([blob], doc.name, { type: doc.type });
      const { base64 } = await documentToBase64(file);

      const { data } = await axios.post(`${API}/analyze-document`, {
        image_base64: base64,
      });
      consume();
      setRemaining(getRemaining());

      // Auto-renommage du document en fonction du contenu détecté par Zerux
      const generated = data.letter || "";
      if (generated) {
        try {
          const parsed = parseLetter(generated);
          if (parsed.title && parsed.title !== doc.name) {
            const ext = doc.name.includes(".") ? "." + doc.name.split(".").pop() : "";
            const cleanTitle = parsed.title.replace(/[/\\?%*:|"<>]/g, "-");
            const newName = ext && !cleanTitle.endsWith(ext) ? `${cleanTitle}${ext}` : cleanTitle;
            await renameDocument(doc.id, newName);
            await refresh();
          }
        } catch { /* silent */ }
      }

      if (onLetterGenerated) onLetterGenerated(generated, data.summary || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors de l'analyse.";
      toast.error(msg);
    } finally {
      setAnalyzingId(null);
    }
  };

  if (!open) return null;

  const totalSize = docs.reduce((s, d) => s + d.size, 0);

  return (
    <div
      data-testid="coffre-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 fade-up"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-md"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.3)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Vault size={20} strokeWidth={1.4} style={{ color: "#D4AF37" }} />
            <div>
              <div className="font-serif-display text-xl tracking-tight">
                Mon Coffre
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Stockage privé · navigateur uniquement
              </div>
            </div>
          </div>
          <button
            data-testid="coffre-close-button"
            onClick={onClose}
            className="text-zinc-500 hover:text-[#D4AF37] transition-colors"
          >
            <X size={20} strokeWidth={1.6} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {!created ? (
            <div className="text-center py-10 fade-up">
              <div
                className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-5 gold-pulse"
                style={{
                  background: "#0A0A0A",
                  border: "1px solid rgba(212,175,55,0.5)",
                }}
              >
                <Vault size={26} strokeWidth={1.4} style={{ color: "#D4AF37" }} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
                Première utilisation
              </div>
              <h3 className="font-serif-display text-2xl mb-3">
                Créez votre coffre personnel
              </h3>
              <p className="text-sm text-zinc-400 max-w-md mx-auto mb-5 leading-relaxed">
                Votre coffre est stocké <strong className="text-zinc-200">uniquement
                dans votre navigateur</strong>. Aucun document ne transite par le serveur,
                ni n&rsquo;est partagé. Conforme RGPD à 100 %.
              </p>

              {/* What it's for — quick benefits */}
              <div className="max-w-md mx-auto mb-6 text-left space-y-2 text-[12px] text-zinc-400 leading-relaxed">
                <div className="flex items-start gap-2.5">
                  <span className="text-[#D4AF37] mt-0.5 shrink-0">✓</span>
                  <span><strong className="text-zinc-200">Stockez</strong> vos courriers reçus, factures, justificatifs administratifs.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-[#D4AF37] mt-0.5 shrink-0">✓</span>
                  <span><strong className="text-zinc-200">Analysez</strong> un document en 1 clic — Zerux génère la lettre de réponse automatiquement.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-[#D4AF37] mt-0.5 shrink-0">✓</span>
                  <span><strong className="text-zinc-200">Retrouvez</strong> vos documents à tout moment, téléchargez‑les ou supprimez‑les en 1 clic.</span>
                </div>
              </div>

              <button
                data-testid="coffre-create-button"
                onClick={handleCreate}
                disabled={busy}
                className="btn-gold-solid inline-flex items-center gap-2 h-11 px-6 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold"
              >
                <Sparkles size={14} strokeWidth={1.8} />
                {busy ? "Création..." : "Créer mon coffre"}
              </button>
            </div>
          ) : (
            <>
              {/* Tabs — 3 sections claires */}
              <div
                data-testid="coffre-tabs"
                className="mb-5 grid grid-cols-3 gap-2 rounded-sm p-1"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
              >
                <TabButton
                  active={tab === "documents"}
                  onClick={() => setTab("documents")}
                  icon={<Inbox size={13} strokeWidth={1.7} />}
                  label="Documents"
                  count={docs.length}
                  testId="tab-documents"
                />
                <TabButton
                  active={tab === "ready"}
                  onClick={() => setTab("ready")}
                  icon={<FileCheck size={13} strokeWidth={1.7} />}
                  label="Lettre prête"
                  count={readyLetters.length}
                  highlight={readyLetters.length > 0 && tab !== "ready"}
                  testId="tab-ready"
                />
                <TabButton
                  active={tab === "archive"}
                  onClick={() => setTab("archive")}
                  icon={<Archive size={13} strokeWidth={1.7} />}
                  label="Stockage"
                  count={archived.length}
                  testId="tab-archive"
                />
              </div>

              {tab === "documents" && (
                <DocumentsSection
                  docs={docs}
                  totalSize={totalSize}
                  busy={busy}
                  handlePickFile={handlePickFile}
                  handleFileChange={handleFileChange}
                  handleAnalyze={handleAnalyze}
                  handleDownload={handleDownload}
                  handleDelete={handleDelete}
                  fileInputRef={fileInputRef}
                  remaining={remaining}
                  analyzingId={analyzingId}
                  renamingDocId={renamingDocId}
                  renameDocValue={renameDocValue}
                  setRenameDocValue={setRenameDocValue}
                  beginRenameDoc={beginRenameDoc}
                  commitRenameDoc={commitRenameDoc}
                />
              )}

              {tab === "ready" && (
                <ReadySection
                  letters={readyLetters}
                  renamingId={renamingId}
                  renameValue={renameValue}
                  setRenameValue={setRenameValue}
                  beginRename={beginRename}
                  commitRename={commitRename}
                  onValidate={handleValidateReady}
                  onCopy={handleCopyLetter}
                  onDownload={handleDownloadLetter}
                  onDelete={handleDeleteLetter}
                />
              )}

              {tab === "archive" && (
                <ArchiveSection
                  letters={archived}
                  renamingId={renamingId}
                  renameValue={renameValue}
                  setRenameValue={setRenameValue}
                  beginRename={beginRename}
                  commitRename={commitRename}
                  onCopy={handleCopyLetter}
                  onDownload={handleDownloadLetter}
                  onDelete={handleDeleteLetter}
                  onDownloadAllZip={handleDownloadAllZip}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/5 text-[10px] text-zinc-600 flex items-center gap-2">
          <ShieldCheck size={11} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
          <span>
            Vos documents restent sur votre appareil. Aucun envoi serveur — conforme RGPD.
          </span>
        </div>
      </div>

      {/* Boîtes de confirmation stylées (remplace window.confirm) */}
      <ConfirmDialog
        open={confirmState?.type === "delete"}
        title="Supprimer le document ?"
        message={
          confirmState?.doc
            ? `« ${confirmState.doc.name} » sera définitivement retiré de votre coffre. Cette action est irréversible.`
            : ""
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        tone="danger"
        icon="warning"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmState(null)}
      />
      <ConfirmDialog
        open={confirmState?.type === "analyze"}
        title="Analyser ce document ?"
        message={
          confirmState?.doc ? (
            <>
              <div className="mb-2">
                Envoi temporaire de <strong className="text-zinc-100">« {confirmState.doc.name} »</strong> à l&rsquo;IA pour générer une lettre de réponse.
              </div>
              <ul className="space-y-1.5 text-[12px] text-zinc-400">
                <li className="flex gap-2"><span style={{color:'#D4AF37'}}>•</span> Aucun stockage — ni par HELP&rsquo;ADN, ni par le fournisseur d&rsquo;IA.</li>
                <li className="flex gap-2"><span style={{color:'#D4AF37'}}>•</span> Il vous restera <strong className="text-zinc-100">{Math.max(0, getRemaining() - 1)}</strong> analyse(s) sur {DAILY_LIMIT} pour aujourd&rsquo;hui.</li>
              </ul>
            </>
          ) : ""
        }
        confirmLabel="Lancer l'analyse"
        cancelLabel="Annuler"
        tone="gold"
        icon="shield"
        onConfirm={confirmAnalyze}
        onCancel={() => setConfirmState(null)}
      />
      <ConfirmDialog
        open={confirmState?.type === "delete-letter"}
        title="Supprimer cette lettre ?"
        message="Cette lettre sera définitivement supprimée du stockage. Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        tone="danger"
        icon="warning"
        onConfirm={confirmDeleteLetter}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}

/* ---------------- Sous-composants ---------------- */

function TabButton({ active, onClick, icon, label, count, highlight, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="relative h-9 px-3 rounded-sm text-[10px] uppercase tracking-[0.2em] font-medium flex items-center justify-center gap-1.5 transition-colors"
      style={{
        background: active ? "rgba(212,175,55,0.12)" : "transparent",
        color: active ? "#F3E5AB" : "#a1a1aa",
        border: active ? "1px solid rgba(212,175,55,0.4)" : "1px solid transparent",
      }}
    >
      {icon}
      <span>{label}</span>
      {typeof count === "number" && (
        <span
          className="text-[9px] px-1.5 py-0.5 rounded-sm ml-0.5"
          style={{
            background: active ? "rgba(212,175,55,0.2)" : "rgba(255,255,255,0.05)",
            color: active ? "#D4AF37" : "#71717a",
          }}
        >
          {count}
        </span>
      )}
      {highlight && (
        <span
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
          style={{ background: "#D4AF37", boxShadow: "0 0 8px #D4AF37" }}
        />
      )}
    </button>
  );
}

function DocumentsSection({
  docs, totalSize, busy, handlePickFile, handleFileChange, handleAnalyze,
  handleDownload, handleDelete, fileInputRef, remaining, analyzingId,
  renamingDocId, renameDocValue, setRenameDocValue, beginRenameDoc, commitRenameDoc,
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          <span style={{ color: "#D4AF37" }}>{docs.length}</span> document
          {docs.length > 1 ? "s" : ""} ·{" "}
          <span style={{ color: "#D4AF37" }}>{formatBytes(totalSize)}</span> utilisés
        </div>
        <button
          data-testid="coffre-add-button"
          onClick={handlePickFile}
          disabled={busy}
          className="btn-gold-solid h-10 px-4 rounded-sm text-xs uppercase tracking-[0.2em] font-medium flex items-center gap-2"
        >
          <Upload size={14} strokeWidth={1.8} />
          Ajouter
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
          className="hidden"
          data-testid="coffre-file-input"
          onChange={handleFileChange}
        />
      </div>

      <div
        data-testid="coffre-quota"
        className="mb-5 px-3 py-2 rounded-sm flex items-center justify-between gap-3 text-[11px]"
        style={{ background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)" }}
      >
        <div className="flex items-center gap-2 text-zinc-400">
          <Wand2 size={12} strokeWidth={1.8} style={{ color: "#D4AF37" }} />
          <span>Analyses IA aujourd&rsquo;hui&nbsp;:</span>
          <span style={{ color: "#F3E5AB" }}>{DAILY_LIMIT - remaining} / {DAILY_LIMIT}</span>
        </div>
        <div className="text-zinc-600 text-[10px] uppercase tracking-[0.2em]">Reset 00 h 00</div>
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-sm">
          Aucun document déposé. Cliquez sur <strong className="text-zinc-300">Ajouter</strong> pour uploader
          un courrier reçu (photo ou PDF) et générer sa réponse.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="coffre-docs-list">
          {docs.map((d, i) => (
            <li
              key={d.id}
              data-testid={`coffre-doc-${i}`}
              className="rounded-sm px-4 py-3 flex items-center gap-3"
              style={{ background: "rgba(212,175,55,0.03)", border: "1px solid rgba(212,175,55,0.12)" }}
            >
              <FileText size={20} strokeWidth={1.3} style={{ color: "#D4AF37" }} className="shrink-0" />
              <div className="flex-1 min-w-0">
                {renamingDocId === d.id ? (
                  <input
                    data-testid={`coffre-doc-rename-input-${i}`}
                    autoFocus
                    value={renameDocValue}
                    onChange={(e) => setRenameDocValue(e.target.value)}
                    onBlur={commitRenameDoc}
                    onKeyDown={(e) => e.key === "Enter" && commitRenameDoc()}
                    className="w-full glass-input rounded-sm px-2 py-1 text-sm text-zinc-100 outline-none focus:border-[#D4AF37]/50"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-zinc-100 truncate">{d.name}</div>
                    <button
                      data-testid={`coffre-doc-rename-${i}`}
                      onClick={() => beginRenameDoc(d)}
                      className="text-zinc-500 hover:text-[#D4AF37] transition-colors shrink-0"
                      title="Renommer"
                    >
                      <Pencil size={11} strokeWidth={1.8} />
                    </button>
                  </div>
                )}
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {formatBytes(d.size)} · {new Date(d.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              </div>
              <button
                data-testid={`coffre-analyze-${i}`}
                onClick={() => handleAnalyze(d)}
                disabled={analyzingId === d.id || remaining <= 0}
                className="btn-gold-solid h-9 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Wand2 size={12} strokeWidth={1.8} />
                <span className="hidden sm:inline">{analyzingId === d.id ? "Analyse..." : "Analyser"}</span>
              </button>
              <button
                data-testid={`coffre-download-${i}`}
                onClick={() => handleDownload(d)}
                className="btn-gold h-9 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5"
              >
                <Download size={12} strokeWidth={1.8} />
                <span className="hidden sm:inline">Télécharger</span>
              </button>
              <button
                data-testid={`coffre-delete-${i}`}
                onClick={() => handleDelete(d)}
                className="h-9 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 text-red-400 hover:text-white hover:bg-red-500/80 border border-red-500/40 hover:border-red-500 transition-colors"
              >
                <Trash2 size={12} strokeWidth={1.8} />
                <span className="hidden sm:inline">Supprimer</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function LetterCard({
  record, isRenaming, renameValue, setRenameValue, beginRename, commitRename,
  onCopy, onDownload, onDelete, onValidate, showValidate,
}) {
  return (
    <li
      data-testid={`letter-${record.id}`}
      className="rounded-sm p-4"
      style={{ background: "rgba(212,175,55,0.03)", border: "1px solid rgba(212,175,55,0.15)" }}
    >
      <div className="flex items-start gap-3 mb-3">
        <FileCheck size={18} strokeWidth={1.4} style={{ color: "#D4AF37" }} className="shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <input
              data-testid={`letter-rename-input-${record.id}`}
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => e.key === "Enter" && commitRename()}
              className="w-full glass-input rounded-sm px-2 py-1 text-sm text-zinc-100 outline-none focus:border-[#D4AF37]/50"
            />
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-sm text-zinc-100 truncate font-medium">{record.title}</div>
              <button
                data-testid={`letter-rename-${record.id}`}
                onClick={() => beginRename(record)}
                className="text-zinc-500 hover:text-[#D4AF37] transition-colors shrink-0"
                title="Renommer"
              >
                <Pencil size={12} strokeWidth={1.8} />
              </button>
            </div>
          )}
          <div className="text-[10px] text-zinc-500 mt-1 space-y-0.5">
            {record.preview?.recipient && (
              <div><span className="text-zinc-600">Destinataire :</span> {record.preview.recipient}</div>
            )}
            {record.preview?.object && (
              <div><span className="text-zinc-600">Objet :</span> {record.preview.object}</div>
            )}
            <div><span className="text-zinc-600">Date :</span> {record.preview?.date}</div>
          </div>
          {record.preview?.excerpt && (
            <div className="mt-2 text-[11px] text-zinc-400 italic line-clamp-2 leading-relaxed">
              « {record.preview.excerpt} »
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap justify-end">
        <button
          data-testid={`letter-copy-${record.id}`}
          onClick={() => onCopy(record.letter)}
          className="btn-gold h-8 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5"
        >
          <Copy size={11} strokeWidth={1.8} /> Copier
        </button>
        <button
          data-testid={`letter-pdf-${record.id}`}
          onClick={() => onDownload(record)}
          className="btn-gold h-8 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5"
        >
          <Download size={11} strokeWidth={1.8} /> PDF
        </button>
        {showValidate && (
          <button
            data-testid={`letter-validate-${record.id}`}
            onClick={() => onValidate(record.id)}
            className="btn-gold-solid h-8 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5"
            title="Valider et déplacer dans le stockage"
          >
            <CheckCircle2 size={11} strokeWidth={1.8} /> Valider &amp; archiver
          </button>
        )}
        <button
          data-testid={`letter-delete-${record.id}`}
          onClick={() => onDelete(record.id)}
          className="h-8 px-3 rounded-sm text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 text-red-400 hover:text-white hover:bg-red-500/80 border border-red-500/40 hover:border-red-500 transition-colors"
        >
          <Trash2 size={11} strokeWidth={1.8} /> Supprimer
        </button>
      </div>
    </li>
  );
}

function ReadySection({
  letters, renamingId, renameValue, setRenameValue,
  beginRename, commitRename, onValidate, onCopy, onDownload, onDelete,
}) {
  if (letters.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500 text-sm">
        <FileCheck size={24} strokeWidth={1.2} style={{ color: "#D4AF37" }} className="mx-auto mb-3 opacity-60" />
        Aucune lettre prête pour le moment. Générez une lettre depuis le chat ou analysez un document —
        elle apparaîtra ici pour validation.
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="ready-letters-list">
      {letters.map((r) => (
        <LetterCard
          key={r.id}
          record={r}
          isRenaming={renamingId === r.id}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          beginRename={beginRename}
          commitRename={commitRename}
          onCopy={onCopy}
          onDownload={onDownload}
          onDelete={onDelete}
          onValidate={onValidate}
          showValidate
        />
      ))}
    </ul>
  );
}

function ArchiveSection({
  letters, renamingId, renameValue, setRenameValue,
  beginRename, commitRename, onCopy, onDownload, onDelete, onDownloadAllZip,
}) {
  if (letters.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500 text-sm">
        <Archive size={24} strokeWidth={1.2} style={{ color: "#D4AF37" }} className="mx-auto mb-3 opacity-60" />
        Le stockage est vide. Validez une lettre depuis <strong className="text-zinc-300">Lettre prête</strong> pour l&rsquo;archiver ici.
      </div>
    );
  }
  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          <span style={{ color: "#D4AF37" }}>{letters.length}</span> lettre{letters.length > 1 ? "s" : ""} archivée{letters.length > 1 ? "s" : ""}
        </div>
        <button
          data-testid="download-all-zip"
          onClick={onDownloadAllZip}
          className="btn-gold h-9 px-3 rounded-sm text-[10px] uppercase tracking-[0.2em] font-medium flex items-center gap-1.5"
          title="Regrouper toutes les lettres archivées dans un fichier ZIP"
        >
          <FileArchive size={12} strokeWidth={1.8} />
          Tout télécharger (ZIP)
        </button>
      </div>
      <ul className="space-y-3" data-testid="archive-letters-list">
        {letters.map((r) => (
          <LetterCard
            key={r.id}
            record={r}
            isRenaming={renamingId === r.id}
            renameValue={renameValue}
            setRenameValue={setRenameValue}
            beginRename={beginRename}
            commitRename={commitRename}
            onCopy={onCopy}
            onDownload={onDownload}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </>
  );
}
